/**
 * Reject (request changes on) a report.
 *
 * Sends the report back to `draft` with a rejection reason + free-form
 * note for the delivery team. SPEC §7.5 + Decision #14: rejection is a
 * workflow step, not a destructive action — the row stays, the audit
 * trail is captured on the report itself, and (later) a system_event
 * message lands on the contract's review thread.
 */

import { ArrowUpDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { ReportRejectionReason } from '@beacon/core';

import { Button } from '@/components/ui/button.js';
import { Textarea } from '@/components/ui/textarea.js';
import { useRejectReport } from '@/hooks/use-reports.js';
import { cn } from '@/lib/cn.js';

interface Props {
  open: boolean;
  onClose: () => void;
  reportId: string;
  contractName: string;
  periodLabel: string;
}

interface ReasonOption {
  id: ReportRejectionReason;
  label: string;
  sub: string;
}

const REASONS: ReasonOption[] = [
  {
    id: 'hours_mismatch',
    label: "Hours don't reconcile to Harvest",
    sub: 'Numbers in draft differ from Harvest source',
  },
  {
    id: 'thin_notes',
    label: 'Time-entry notes need detail',
    sub: 'Client-facing copy is too terse',
  },
  {
    id: 'attribution',
    label: 'Wrong attribution',
    sub: 'Entry should be on a different ticket / contract',
  },
  {
    id: 'duplicate',
    label: 'Possible duplicate entry',
    sub: 'Two entries appear to cover the same work',
  },
  {
    id: 'commercial',
    label: 'Commercial concern',
    sub: 'Over allocation, billing surprise, etc.',
  },
  {
    id: 'other',
    label: 'Other',
    sub: 'Free-form reason in the note below',
  },
];

export function RejectReportDialog({
  open,
  onClose,
  reportId,
  contractName,
  periodLabel,
}: Props) {
  const reject = useRejectReport(reportId);
  const [reason, setReason] = useState<ReportRejectionReason>('hours_mismatch');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('hours_mismatch');
    setNote('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !reject.isPending) onClose();
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === 'Enter' &&
        !reject.isPending
      ) {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reject.isPending, reason, note]);

  if (!open) return null;

  function handleSubmit() {
    const trimmed = note.trim();
    if (!trimmed) return toast.error('A note for the delivery team is required');
    reject.mutate(
      { reason, note: trimmed },
      {
        onSuccess: () => {
          toast.success('Sent back', {
            description: 'Report is back in draft for changes.',
          });
          onClose();
        },
      }
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dialog-in-backdrop"
      onClick={reject.isPending ? undefined : onClose}
    >
      <div
        className="w-full max-w-2xl mx-4 rounded-lg border border-ink-600 bg-ink-850 shadow-xl max-h-[92vh] overflow-y-auto dialog-in-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reject-report-title"
      >
        <header className="px-5 pt-4 pb-3 border-b border-ink-700 flex items-start gap-3">
          <span className="w-9 h-9 rounded-md bg-warn-500/15 text-warn-400 border border-warn-500/30 inline-flex items-center justify-center shrink-0">
            <ArrowUpDown className="h-4 w-4" />
          </span>
          <div className="flex-1 min-w-0">
            <h2
              id="reject-report-title"
              className="text-[15px] font-semibold text-ink-100"
            >
              Request changes
            </h2>
            <p className="text-[12.5px] text-ink-300 mt-0.5 truncate">
              Sends{' '}
              <span className="mono text-ink-100">
                {contractName} · {periodLabel}
              </span>{' '}
              back to draft. No client email is sent.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={reject.isPending}
            className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-4">
          <Field label="Primary reason">
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setReason(r.id)}
                  disabled={reject.isPending}
                  data-testid={`rrd-reason-${r.id}`}
                  className={cn(
                    'text-left px-3 py-2 rounded-md border focus-ring',
                    reason === r.id
                      ? 'bg-warn-500/10 border-warn-500/40'
                      : 'border-ink-600 bg-ink-800 hover:border-ink-500'
                  )}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className={cn(
                        'w-3 h-3 rounded-full border-2',
                        reason === r.id
                          ? 'border-warn-400'
                          : 'border-ink-500'
                      )}
                    >
                      {reason === r.id && (
                        <span className="block w-1.5 h-1.5 rounded-full bg-warn-400 m-[1px]" />
                      )}
                    </span>
                    <span className="text-[12.5px] text-ink-100">
                      {r.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-ink-400 ml-[18px]">
                    {r.sub}
                  </div>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Note for delivery team">
            <Textarea
              rows={6}
              value={note}
              disabled={reject.isPending}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What needs to change? Be specific — this lands on the report row for the next round of edits."
              data-testid="rrd-note"
            />
          </Field>
        </div>

        <footer className="px-5 py-3 border-t border-ink-700 flex items-center gap-2">
          <span className="text-[11.5px] text-ink-400 mono">
            ⌘↵ send back · esc cancel
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={reject.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={reject.isPending || !note.trim()}
              data-testid="rrd-submit"
              className="!bg-warn-500 !text-ink-900 hover:!bg-warn-400 !border-warn-500"
            >
              <ArrowUpDown className="h-3.5 w-3.5" />
              {reject.isPending ? 'Sending…' : 'Send back'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
